import React, { useContext, useState, useEffect, useReducer, useRef, memo, useCallback } from "react";
import { getMediaUrl } from "../../utils/mediaUrl";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green, red } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Badge
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  ErrorOutline,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  Close,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";
import ListPreview from "../ListPreview";
import PixPreview from "../PixPreview";
import ButtonPreview from "../ButtonPreview";
import EventPreview from "../EventPreview";
import InteractiveButtonsPreview from "../InteractiveButtonsPreview";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import AdMetaPreview from "../AdMetaPreview"; // Adicionado componente de preview de anúncio
// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { messages } from "../../translate/languages";
import { useParams, useHistory } from 'react-router-dom';
import { downloadResource } from "../../utils";

const getGroupSenderName = (message) => {
  try {
    if (message.dataJson) {
      const data = JSON.parse(message.dataJson);
      if (data?.pushName) return data.pushName;
    }
  } catch (_) {}
  return message.contact?.name || "";
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: theme.palette.primary.main,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "1px 5px 10px #b3b3b3",
  },

  currentTicktText: {
    color: theme.palette.primary,
    fontWeight: 'bold',
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: 'rgba(255, 255, 255, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: "5px dashed #333",
    color: '#333',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "min(85%, 500px)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    boxShadow: theme.mode === 'light' ? "0 1px 2px rgba(0,0,0,0.1)" : "0 1px 2px rgba(0,0,0,0.3)"
  },

  quotedContainerLeft: {
    margin: "4px 0 8px 0",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: 8,
    display: "flex",
    position: "relative",
    maxWidth: "100%",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "min(85%, 500px)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 0,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    boxShadow: theme.mode === 'light' ? "0 1px 2px rgba(0,0,0,0.1)" : "0 1px 2px rgba(0,0,0,0.3)"
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: "min(85%, 500px)",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 0,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    boxShadow: theme.mode === 'light' ? "0 1px 2px rgba(0,0,0,0.1)" : "0 1px 2px rgba(0,0,0,0.3)"
  },

  quotedContainerRight: {
    margin: "4px 0 8px 0",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: 8,
    display: "flex",
    position: "relative",
    maxWidth: "100%",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    padding: "3px 0px 3px 0px",
    maxWidth: "100%",
    overflow: "hidden",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 6px 3px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: "100%",
    maxWidth: 350,
    height: "auto",
    borderRadius: 8,
    display: "block",
  },

  timestamp: {
    fontSize: 11,
    color: "#999",
    whiteSpace: "nowrap",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 4,
    gap: 2,
  },

  senderName: {
    fontWeight: 500,
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    position: "absolute",
    top: 0,
    left: 5,
    color: "#999",
    display: "flex",
    alignItems: "center"
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.mode === 'light' ? "#f5f5f5" : "#1a2c38",
    padding: 12,
    borderRadius: 8,
    marginTop: 4,
    marginBottom: 4,
    color: theme.mode === "light" ? "#333" : "#fff",
    "& .MuiButton-root": {
      textTransform: "none",
      fontWeight: 500,
    },
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {

      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel
}) => {
  const classes = useStyles();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  useEffect(() => {

    async function fetchData() {

      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {

        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    //    const socket = socketManager.GetSocket();
    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    }

    const onAppMessageMessagesList = (data) => {
      if (data.action === "create" && data.ticket.uuid === ticketId) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update" && data?.message?.ticket?.uuid === ticketId) {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action == "delete" && data.message.ticket?.uuid === ticketId) {
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    }
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {

      socket.emit("joinChatBoxLeave", `${ticketId}`)

      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };

  }, [ticketId]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

const scrollToBottom = () => {
  setTimeout(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  }, 100);
};

const handleScroll = (e) => {
  if (!hasMore) return;
  const { scrollTop } = e.currentTarget;

  if (scrollTop === 0) {
    document.getElementById("messagesList").scrollTop = 1;
  }

  if (loading) {
    return;
  }

  if (scrollTop < 50) {
    loadMore();
  }
};

const handleOpenMessageOptionsMenu = (e, message) => {
  setAnchorEl(e.currentTarget);
  setSelectedMessage(message);
};

const handleCloseMessageOptionsMenu = (e) => {
  setAnchorEl(null);
};

const hanldeReplyMessage = (e, message) => {
  //if (ticket.status === "open" || ticket.status === "group") {
  setAnchorEl(null);
  setReplyingMessage(message);
  //}
};

const COLLAPSE_THRESHOLD = 600; // caracteres

const MessageBody = ({ text, deleted, lgpd }) => {
  const [expanded, setExpanded] = useState(false);
  const body = (lgpd && deleted) ? "🚫 _Mensagem apagada_ " : text;
  const isLong = body && body.length > COLLAPSE_THRESHOLD;
  const displayed = isLong && !expanded ? body.slice(0, COLLAPSE_THRESHOLD) : body;

  return (
    <>
      <MarkdownWrapper>{displayed}</MarkdownWrapper>
      {isLong && (
        <span
          onClick={() => setExpanded(e => !e)}
          style={{ display: "block", color: "#2563eb", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", marginTop: 2, userSelect: "none" }}
        >
          {expanded ? "Ler menos" : "... Ler mais"}
        </span>
      )}
    </>
  );
};

const checkMessageMedia = (message) => {
  console.log(message)
  if (message.mediaType === "eventMessage") {
    try {
      // Parsear o dataJson diretamente da coluna do banco de dados
      const parsedData = JSON.parse(message.dataJson);
      const eventMessage = parsedData.message.eventMessage;
  
      const titulo = eventMessage.name || "Sem título";
      const descricao = eventMessage.description || "Sem descrição";
      const local = eventMessage.location?.name || "Local não especificado";
      const link = eventMessage.joinLink || "#";
      const linkTipo = link.includes("video") ? "video" : "voice";
  
      // Converter timestamps para data/hora formatada
      const startTime = Number(eventMessage.startTime); // Em segundos
      const endTime = startTime + 7200; // Exemplo: reunião de 2h
  
      const formatDateTime = (timestamp) => {
        const date = new Date(timestamp * 1000);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return `${day}/${month}/${year}, ${time}`;
      };
  
      return (
        <EventPreview
          name={titulo}
          description={descricao}
          location={local}
          startTime={startTime}
          endTime={endTime}
          joinLink={link}
          linkType={linkTipo}
        />
      );
    } catch (error) {
      console.error("Erro ao processar a mensagem de evento: ", error);
    }
  } else
  if (message.mediaType === "listMessage") {
    try {
      // Parsear o dataJson diretamente da coluna do banco de dados
      const parsedData = JSON.parse(message.dataJson);
      const listMessage = parsedData.message.listMessage;
      let titulo = listMessage.title || "";
      let descricao = listMessage.description || "";
      let rodape = listMessage.footerText || "";
      let secoes = [];
      let rows = [];
      listMessage.sections.forEach((section, index) => {
        let currentSection = section.title || ""; 
        section.rows.forEach((row, rowIndex) => {
          rows.push({
            titulo: row.title,           // Título do item da linha
            descricao: row.description,  // Descrição do item da linha
            idLinha: row.rowId           // ID da linha
          });
        });
        secoes.push({ titulo: currentSection, linhas: rows });
        rows = [];
      });
      return (
        <ListPreview
          titulo={titulo}
          descricao={descricao}
          textoBotao={listMessage.buttonText || "Clique aqui"} // Texto do botão
          secoes={secoes}
          rodape={rodape}
          ticketId={message?.ticket?.id}
        />
      );
    } catch (error) {
      console.error("Erro ao processar a lista de mensagens: ", error);
    }
  }
   else
   if (message.mediaType === "viewOnceMessage" && message.body.startsWith('[BOTOES]')) {
    try {
        const dataJsonString = message.dataJson;
        if (!dataJsonString || typeof dataJsonString !== 'string') {
            console.error("dataJson não está definido ou não é uma string válida.");
            return; 
        }
        const parsedData = JSON.parse(dataJsonString); 
        const viewOnceMessage = parsedData.message.viewOnceMessage.message.interactiveMessage; // Acessando a mensagem diretamente
        if (!viewOnceMessage) {
            console.error("viewOnceMessage não está definido.");
            return; // Sai da função se viewOnceMessage não estiver definido
        }
        const titulo = viewOnceMessage.body?.text || "";  // Título
        const rodape = viewOnceMessage.footer?.text || "";  // Rodapé
        const botoes = viewOnceMessage.nativeFlowMessage?.buttons?.map(button => {
            const { name, buttonParamsJson } = button;
            const params = JSON.parse(buttonParamsJson);  // Parse JSON para obter o conteúdo
            return {
                tipo: name,
                texto: params.display_text,
                conteudo: params.phoneNumber || params.url || params.copy_code
            };
        }) || [];  
        let imagem = null;
        if (viewOnceMessage.header?.imageMessage?.jpegThumbnail) {
            imagem = viewOnceMessage.header.imageMessage.jpegThumbnail;    
        } else {
            console.log("Nenhuma imagem encontrada no header.");
        }
        return (
            <ButtonPreview 
                titulo={titulo} 
                rodape={rodape} 
                secoes={[{ titulo: "Botões", linhas: botoes }]} 
                imagem={imagem} 
                ticketId={message?.ticket?.id} 
            />
        );
    } catch (error) {
        console.error("Erro ao processar a mensagem do tipo viewOnceMessage: ", error);
    }
} else
  if (message.mediaType === "interactiveMessage" && !message.body.startsWith('[PIX]')) {
    try {
      let buttons = null;
      if (message.dataJson) {
        const parsed = JSON.parse(message.dataJson);
        buttons = parsed.buttonsData;
      }
      if (buttons && buttons.length > 0) {
        return (
          <InteractiveButtonsPreview
            body={message.body}
            buttons={buttons}
            ticketId={message?.ticket?.id}
            fromMe={message.fromMe}
          />
        );
      }
    } catch (_) {}
  } else
  if ((message.mediaType === "viewOnceMessage" || message.mediaType === "interactiveMessage") && message.body.startsWith('[PIX]')) {
  try {
      // Verifica se o dataJson existe e é uma string antes de tentar fazer o parse
      const dataJsonString = message.dataJson;
      if (!dataJsonString || typeof dataJsonString !== 'string') {
          console.error("dataJson não está definido ou não é uma string válida.");
          return; // Sai da função se não for válido
      }
      // Parse do JSON
      const parsedData = JSON.parse(dataJsonString);
      const nativeFlowMessage =
          parsedData.message?.interactiveMessage?.nativeFlowMessage ||
          parsedData.message?.viewOnceMessage?.message?.interactiveMessage?.nativeFlowMessage;
      if (!nativeFlowMessage) {
          console.error("Mensagem de fluxo nativa não encontrada.");
          return; // Sai da função se não encontrar o fluxo nativo
      }
      const button = nativeFlowMessage.buttons?.[0]; // Considera que há um único botão
      if (!button) {
          console.error("Botão não encontrado.");
          return;
      }
      const { name, buttonParamsJson } = button;
      const params = JSON.parse(buttonParamsJson);
      const numeroCobranca = params.reference_id || "N/A";
      const total = params.total_amount?.value || "N/A";
      const produto = params.order?.items?.[0]?.name || "N/A";
      let imagem = null;
      if (nativeFlowMessage.header?.imageMessage?.jpegThumbnail) {
          imagem = nativeFlowMessage.header.imageMessage.jpegThumbnail;
      } else {
      }
      // Renderizar o componente PixPreview
      return (
          <PixPreview
              companyId={message.companyId}
              avatarUser={message.ticket?.user?.profileImage}
              avatarName={message.ticket?.user?.name}
              avatarUrl={message.contact?.urlPicture}
              name={message.contact?.name}  
              numeroCobranca={numeroCobranca}
              total={total}
              produto={produto}
              imagem={imagem}
              ticketId={message?.ticket?.id}
          />
      );
  } catch (error) {
      console.error("Erro ao processar a mensagem de pagamento: ", error);
  }
} else
  if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
    let locationParts = message.body.split('|')
    let imageLocation = locationParts[0]
    let linkLocation = locationParts[1]

    let descriptionLocation = null

    if (locationParts.length > 2)
      descriptionLocation = message.body.split('|')[2]

    return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
  } else

    if (message.mediaType === "contactMessage") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      // console.log(message)
      return <VcardPreview contact={contact} numbers={obj[0]?.number} queueId={message?.ticket?.queueId} whatsappId={message?.ticket?.whatsappId} />
    } 
    else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
      console.log("Entrou no MetaPreview");
      let [image, sourceUrl, title, body, messageUser] = message.body.split('|');
      return <AdMetaPreview image={image} sourceUrl={sourceUrl} title={title} body={body} messageUser={messageUser} />;
  }
  // Tratar mensagens de visualização única (viewOnce) como mídia normal
  else if (message.mediaType === "viewOnceMessageV2" && message.mediaUrl) {
    // Detectar o tipo de mídia pela URL ou mimetype
    const mediaUrl = message.mediaUrl.toLowerCase();
    if (mediaUrl.includes('.jpg') || mediaUrl.includes('.jpeg') || mediaUrl.includes('.png') || mediaUrl.includes('.gif') || mediaUrl.includes('.webp') || mediaUrl.includes('image')) {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (mediaUrl.includes('.mp4') || mediaUrl.includes('.mov') || mediaUrl.includes('.avi') || mediaUrl.includes('video')) {
      return (
        <video
          className={classes.messageMedia}
          src={getMediaUrl(message.mediaUrl)}
          controls
        />
      );
    } else if (mediaUrl.includes('.ogg') || mediaUrl.includes('.mp3') || mediaUrl.includes('.opus') || mediaUrl.includes('audio') || mediaUrl.includes('ptt')) {
      return <AudioModal url={message.mediaUrl} />;
    } else {
      // Fallback para download
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              target="_blank"
              onClick={() => {
                downloadResource(message.mediaUrl || message.body)
              }}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  }
  else if (message.mediaType === "image") {
        return (
          <div style={{ position: "relative", display: "inline-block" }}>
            <ModalImageCors imageUrl={message.mediaUrl} />
            {message.loading && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: 8, gap: 4 }}>
                <CircularProgress size={24} style={{ color: "#fff" }} />
                <span style={{ color: "#fff", fontSize: 11 }}>Enviando...</span>
              </div>
            )}
          </div>
        );
      } else

        if (message.mediaType === "audio") {
          return (
            <div style={{ position: "relative" }}>
              <AudioModal url={message.mediaUrl} />
              {message.loading && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, color: "#888", fontSize: 11 }}>
                  <CircularProgress size={12} />
                  <span>Enviando áudio...</span>
                </div>
              )}
            </div>
          );
        } else

          if (message.mediaType === "video") {
            return (
              <div style={{ position: "relative" }}>
                <video
                  className={classes.messageMedia}
                  src={getMediaUrl(message.mediaUrl)}
                  controls={!message.loading}
                  style={{ opacity: message.loading ? 0.5 : 1 }}
                />
                {message.loading && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: 8, gap: 6 }}>
                    <CircularProgress size={32} style={{ color: "#fff" }} />
                    <span style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>Enviando vídeo...</span>
                  </div>
                )}
              </div>
            );
          } else {
            return (
              <>
                <div className={classes.downloadMedia}>
                  <Button
                    startIcon={<GetApp />}
                    variant="outlined"
                    target="_blank"
                    onClick={() => {
                      downloadResource(message.mediaUrl || message.body)
                    }}
                  >
                    Download
                  </Button>
                </div>
                <Divider />
              </>
            );
          }
};

const renderMessageAck = (message) => {
  // ack -1 = a operadora recusou o envio. Sem este caso a mensagem ficava
  // sem icone nenhum e passava por entregue — o atendente so descobria a
  // falha indo conferir no aplicativo do cliente.
  if (message.ack === -1) {
    return (
      <ErrorOutline
        fontSize="small"
        titleAccess="Não foi entregue ao cliente"
        style={{ color: "#dc2626", verticalAlign: "middle" }}
      />
    );
  }
  if (message.ack === 0) {
    return <AccessTime fontSize="small" className={classes.ackIcons} />;
  } else
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    } else
      if (message.ack === 2) {
        return <DoneAll fontSize="small" className={classes.ackIcons} />;
      } else
        if (message.ack === 3 || message.ack === 4) {
          return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
        } else
          if (message.ack === 5) {
            return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
          }
};

const renderDailyTimestamps = (message, index) => {
  const today = format(new Date(), "dd/MM/yyyy")

  if (index === 0) {
    return (
      <span
        className={classes.dailyTimestamp}
        key={`timestamp-${message.id}`}
      >
        <div className={classes.dailyTimestampText}>
          {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
        </div>
      </span>
    );
  } else
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    } else
      if (index === messagesList.length - 1) {
        return (
          <div
            key={`ref-${message.id}`}
            ref={lastMessageRef}
            style={{ float: "left", clear: "both" }}
          />
        );
      }
};


const renderTicketsSeparator = (message, index) => {
  let lastTicket = messagesList[index - 1]?.ticketId;
  let currentTicket = message.ticketId;

  if (lastTicket !== currentTicket && lastTicket !== undefined) {
    if (message?.ticket?.queue) {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}a`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: message?.ticket?.queue?.color || "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
          </div>

        </span>
      );
    } else {
      return (
        <span
          className={classes.currentTick}
          key={`timestamp-${message.id}b`}
        >
          <div
            className={classes.currentTicktText}
            style={{ backgroundColor: "grey" }}
          >
            #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
          </div>

        </span>
      );
    }
  }

};

const renderMessageDivider = (message, index) => {
  if (index < messagesList.length && index > 0) {
    let messageUser = messagesList[index].fromMe;
    let previousMessageUser = messagesList[index - 1].fromMe;
    if (messageUser !== previousMessageUser) {
      return (

        <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
      );
    }
  }
};

const path = require('path');

const AUDIO_MEDIA_TYPES = new Set(["audio", "ptt", "pttConverted", "voice", "voiceMessage"]);

const buildReactionDescription = (message, isFromMe) => {
  const actorName = isFromMe ? "Você" : message?.contact?.name || "Contato";
  const reactionEmoji = message?.body || "";
  const emojiPart = reactionEmoji ? ` com ${reactionEmoji}` : "";

  if (message?.quotedMsg) {
    const reactedTarget = message.quotedMsg.fromMe
      ? " sua mensagem"
      : message.quotedMsg?.contact?.name
        ? ` a mensagem de ${message.quotedMsg.contact.name}`
        : " a uma mensagem";
    return `${actorName} reagiu${emojiPart}${reactedTarget}`;
  }

  return `${actorName} reagiu${emojiPart}`;
};

const renderQuotedMessage = (message) => {
  const quoted = message?.quotedMsg;
  if (!quoted) return null;

  const renderQuotedContent = () => {
    if (AUDIO_MEDIA_TYPES.has(quoted.mediaType)) {
      if (!quoted.mediaUrl) {
        return <span>Áudio indisponível</span>;
      }

      return (
        <div className={classes.downloadMedia}>
          <AudioModal url={quoted.mediaUrl} />
        </div>
      );
    }

    if (quoted.mediaType === "video" && quoted.mediaUrl) {
      return (
        <video className={classes.messageMedia} src={getMediaUrl(quoted.mediaUrl)} controls />
      );
    }

    if (quoted.mediaType === "image" && quoted.mediaUrl) {
      return <ModalImageCors imageUrl={quoted.mediaUrl} />;
    }

    if (quoted.mediaType === "sticker" && quoted.mediaUrl) {
      return <img src={getMediaUrl(quoted.mediaUrl)} alt="Sticker" className={classes.messageMedia} loading="lazy" />;
    }

    if (quoted.mediaType === "contactMessage") {
      return "Contato";
    }

    if (quoted.mediaType === "application" && quoted.mediaUrl) {
      return (
        <div className={classes.downloadMedia}>
          <Button
            startIcon={<GetApp />}
            variant="outlined"
            target="_blank"
            href={getMediaUrl(quoted.mediaUrl)}
          >
            Download
          </Button>
        </div>
      );
    }

    if (quoted.body) {
      return <MarkdownWrapper>{quoted.body}</MarkdownWrapper>;
    }

    return <span>Mensagem não disponível</span>;
  };

  return (
    <div
      className={clsx(classes.quotedContainerLeft, {
        [classes.quotedContainerRight]: message.fromMe,
      })}
    >
      <span
        className={clsx(classes.quotedSideColorLeft, {
          [classes.quotedSideColorRight]: quoted?.fromMe,
        })}
      ></span>
      <div className={classes.quotedMsg}>
        {!quoted?.fromMe && quoted?.contact?.name && (
          <span className={classes.messageContactName}>{quoted.contact.name}</span>
        )}
        {renderQuotedContent()}
      </div>
    </div>
  );
};

const handleDrag = event => {
  event.preventDefault();
  event.stopPropagation();
  if (event.type === "dragenter" || event.type === "dragover") {
    setDragActive(true);
  } else if (event.type === "dragleave") {
    setDragActive(false);
  }
}

const isYouTubeLink = (url) => {
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  return youtubeRegex.test(url);
};

const handleDrop = event => {
  event.preventDefault();
  event.stopPropagation();
  setDragActive(false);
  if (event.dataTransfer.files && event.dataTransfer.files[0]) {
    if (onDrop) {
      onDrop(event.dataTransfer.files);
    }
  }
}
const xmlRegex = /<([^>]+)>/g;
const boldRegex = /\*(.*?)\*/g;

const formatXml = (xmlString) => {
  // Verifica se o XML contém a assinatura com nome do atendente
  if (boldRegex.test(xmlString)) {
    // Formata o texto dentro da assinatura em negrito
    xmlString = xmlString.replace(boldRegex, "**$1**");
  }
  return xmlString;
};

const renderMessages = () => {

  if (messagesList.length > 0) {
    const viewMessagesList = messagesList.map((message, index) => {
      if (message.mediaType === "call_log") {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div className={classes.messageCenter}>
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {isGroup && (
                <span className={classes.messageContactName}>
                  {getGroupSenderName(message)}
                </span>
              )}
              <div>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                  <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
              </div>
            </div>
          </React.Fragment>
        );
      }

      if (!message.fromMe) {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={classes.messageLeft}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
            >
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}
              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>

              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                  </span>
                  <br />
                </div>
              )}
              {isGroup && (
                <span className={classes.messageContactName}>
                  {getGroupSenderName(message)}
                </span>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}

              {/* aviso de mensagem apagado pelo contato */}

              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >🚫 Essa mensagem foi apagada pelo contato &nbsp;
                  </span>
                </div>
              )}

              {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "pollCreationMessageV3" || message.mediaType === "eventMessage" || message.mediaType === "listMessage" || message.mediaType === "viewOnceMessage" || message.mediaType === "viewOnceMessageV2" || message.mediaType === "interactiveMessage" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard" 
              ) && checkMessageMedia(message)}

              <div className={clsx(classes.textContentItem, {
                [classes.textContentItemDeleted]: message.isDeleted,
              })}>
                {message.quotedMsg && renderQuotedMessage(message)}
                {
                  (
                    (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
                    message.mediaType !== "audio" &&
                    message.mediaType !== "image" &&
                    message.mediaType !== "video" &&
                    message.mediaType != "reactionMessage" &&
                    message.mediaType != "locationMessage" && message.mediaType !== "contactMessage" && message.mediaType != "pollCreationMessageV3" && message.mediaType != "eventMessage" &&
                    message.mediaType != "listMessage" &&
                    message.mediaType != "viewOnceMessage" &&
                    message.mediaType != "viewOnceMessageV2" &&
                    message.mediaType != "interactiveMessage" && message.mediaType !== "adMetaPreview") && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <span>{message.body}</span>

                      )}
                      {!xmlRegex.test(message.body) && (
                        <MessageBody
                          text={message.body}
                          deleted={message.isDeleted}
                          lgpd={lgpdDeleteMessage}
                        />
                      )}

                    </>

                  )}

                {message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {buildReactionDescription(message, false)}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      } else {
        return (
          <React.Fragment key={message.id}>
            {renderDailyTimestamps(message, index)}
            {renderTicketsSeparator(message, index)}
            {renderMessageDivider(message, index)}
            <div
              className={message.isPrivate ? classes.messageRightPrivate : classes.messageRight}
              title={message.queueId && message.queue?.name}
              onDoubleClick={(e) => hanldeReplyMessage(e, message)}
            >
              {showSelectMessageCheckbox && (
                <SelectMessageCheckbox
                  // showSelectMessageCheckbox={showSelectMessageCheckbox}
                  message={message}
                // selectedMessagesList={selectedMessagesList}
                // setSelectedMessagesList={setSelectedMessagesList}
                />
              )}

              <IconButton
                variant="contained"
                size="small"
                id="messageActionsButton"
                disabled={message.isDeleted}
                className={classes.messageActionsButton}
                onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              >
                <ExpandMore />
              </IconButton>
              {message.isForwarded && (
                <div>
                  <span className={classes.forwardMessage}
                  ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
                  </span>
                  <br />
                </div>
              )}
              {isYouTubeLink(message.body) && (
                <>
                  <YouTubePreview videoUrl={message.body} />
                </>
              )}
              {!lgpdDeleteMessage && message.isDeleted && (
                <div>
                  <span className={classes.deletedMessage}
                  >🚫 Essa mensagem foi apagada &nbsp;
                  </span>
                </div>
              )}
              {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || message.mediaType === "pollCreationMessageV3" || message.mediaType === "eventMessage" || message.mediaType === "listMessage" || message.mediaType === "viewOnceMessage" || message.mediaType === "viewOnceMessageV2" || message.mediaType === "interactiveMessage" || message.mediaType === "adMetaPreview" // Adicionado para aceitar o componente de preview de anúncio
                //|| message.mediaType === "multi_vcard" 
              ) && checkMessageMedia(message)}
              <div
                className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}
              >

                {/* {message.isDeleted && (`🚫`)} */}



                {message.quotedMsg && renderQuotedMessage(message)}

                {
                  ((message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl) === message.body) ||
                  (message.mediaType !== "audio" && message.mediaType != "reactionMessage" && message.mediaType != "locationMessage" && message.mediaType !== "contactMessage" && message.mediaType != "pollCreationMessageV3" && message.mediaType != "eventMessage" &&
                    message.mediaType != "listMessage" &&
                    message.mediaType != "viewOnceMessage" &&
                    message.mediaType != "viewOnceMessageV2" &&
                    message.mediaType != "interactiveMessage" && message.mediaType !== "adMetaPreview") && (
                    <>
                      {xmlRegex.test(message.body) && (
                        <div>{formatXml(message.body)}</div>

                      )}
                      {!xmlRegex.test(message.body) && (<MarkdownWrapper>{message.body}</MarkdownWrapper>)}

                    </>
                  )}

                {message.mediaType === "reactionMessage" && (
                  <>
                    <span style={{ marginLeft: "0px" }}>
                      <MarkdownWrapper>
                        {buildReactionDescription(message, true)}
                      </MarkdownWrapper>
                    </span>
                  </>
                )}

                <span className={classes.timestamp}>
                  {(user?.name || message.fromAgent === false) && (
                    <span className={classes.senderName}>
                      {message.fromAgent === false ? "Automação" : user.name} • {format(parseISO(message.createdAt), "dd/MM/yyyy")} •{" "}
                    </span>
                  )}
                  {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  {renderMessageAck(message)}
                </span>
              </div>
            </div>
          </React.Fragment>
        );
      }
    });
    return viewMessagesList;
  } else {
    return <div>Diga olá para seu novo contato!</div>;
  }
};

return (
  <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
    {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}

    <MessageOptionsMenu
      message={selectedMessage}
      anchorEl={anchorEl}
      menuOpen={messageOptionsMenuOpen}
      handleClose={handleCloseMessageOptionsMenu}
      isGroup={isGroup}
      whatsappId={whatsappId}
      queueId={queueId}
    />
    <div
      id="messagesList"
      className={classes.messagesList}
      onScroll={handleScroll}
    >
      {messagesList.length > 0 ?
        renderMessages()
        : []}
    </div>

    {(channel !== "whatsapp" && channel !== undefined) && (
      <div
        style={{
          width: "100%",
          display: "flex",
          padding: "10px",
          alignItems: "center",
          backgroundColor: "#E1F3FB",
        }}
      >
        {channel === "facebook" ? (
          <Facebook />
        ) : (
          <Instagram />
        )}

        <span>
          Você tem 24h para responder após receber uma mensagem, de acordo
          com as políticas do Facebook.
        </span>
      </div>
    )}
    {loading && (
      <div>
        <CircularProgress className={classes.circleLoading} />
      </div>
    )}
  </div>
);
};

export default MessagesList;